/**
 * CategoriesManager - Manage template categories
 *
 * Features:
 * - View form and document categories in tabs
 * - Create new categories
 * - Edit category name, description, color, icon
 * - Reorder categories via drag and drop
 * - Toggle active/inactive status
 * - Delete categories
 */
import { useEffect, useState } from 'react';
import { motion, Reorder } from 'framer-motion';
import {
  cn,
  Button,
  Input,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Badge,
} from '@respectly/ui';
import {
  Plus,
  Tags,
  RefreshCw,
  Pencil,
  Trash2,
  GripVertical,
  FileText,
  ClipboardList,
  Loader2,
  Check,
  X,
} from 'lucide-react';
import {
  useCategoriesStore,
  useCategoriesLoading,
} from '../../stores/useCategoriesStore';
import type {
  TemplateCategoryEntity,
  TemplateCategoryType,
  CreateTemplateCategoryDto,
  UpdateTemplateCategoryDto,
} from '@respectly/shared-types';

// Color options for categories
const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  { value: 'emerald', label: 'Emerald', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  { value: 'rose', label: 'Rose', class: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
  { value: 'amber', label: 'Amber', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'slate', label: 'Slate', class: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300' },
  { value: 'indigo', label: 'Indigo', class: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  { value: 'neutral', label: 'Neutral', class: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-900/30 dark:text-neutral-300' },
];

// Get color class from color value
function getColorClass(color: string): string {
  const option = COLOR_OPTIONS.find((c) => c.value === color);
  const neutralOption = COLOR_OPTIONS.find((c) => c.value === 'neutral');
  return option?.class || neutralOption?.class || 'bg-neutral-100 text-neutral-700';
}

// Category row component for the list
interface CategoryRowProps {
  category: TemplateCategoryEntity;
  onEdit: (category: TemplateCategoryEntity) => void;
  onDelete: (category: TemplateCategoryEntity) => void;
  onToggleActive: (category: TemplateCategoryEntity) => void;
}

function CategoryRow({ category, onEdit, onDelete, onToggleActive }: CategoryRowProps) {
  return (
    <Reorder.Item
      value={category}
      id={category.id}
      className={cn(
        'flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-lg border',
        'border-slate-200 dark:border-slate-700 shadow-sm',
        'hover:shadow-md transition-shadow',
        !category.isActive && 'opacity-50'
      )}
    >
      {/* Drag Handle */}
      <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600">
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Color Badge */}
      <Badge variant="secondary" className={cn('shrink-0', getColorClass(category.color))}>
        {category.name}
      </Badge>

      {/* Slug */}
      <code className="text-xs text-slate-500 dark:text-slate-400 font-mono">
        {category.slug}
      </code>

      {/* Description */}
      <span className="flex-1 text-sm text-slate-600 dark:text-slate-300 truncate">
        {category.description || '-'}
      </span>

      {/* Status */}
      <span
        className={cn(
          'text-xs font-medium px-2 py-1 rounded-full',
          category.isActive
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
        )}
      >
        {category.isActive ? 'Active' : 'Inactive'}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleActive(category)}
          title={category.isActive ? 'Deactivate' : 'Activate'}
        >
          {category.isActive ? (
            <X className="h-4 w-4 text-slate-500" />
          ) : (
            <Check className="h-4 w-4 text-green-500" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onEdit(category)}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(category)}
          className="text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Reorder.Item>
  );
}

// Create/Edit Category Modal
interface CategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: TemplateCategoryEntity | null;
  type: TemplateCategoryType;
  onSave: (dto: CreateTemplateCategoryDto | UpdateTemplateCategoryDto, id?: string) => Promise<void>;
  isSaving: boolean;
}

function CategoryModal({
  open,
  onOpenChange,
  category,
  type,
  onSave,
  isSaving,
}: CategoryModalProps) {
  const isEditing = !!category;

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('neutral');
  const [icon, setIcon] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (category) {
        setSlug(category.slug);
        setName(category.name);
        setDescription(category.description || '');
        setColor(category.color);
        setIcon(category.icon || '');
      } else {
        setSlug('');
        setName('');
        setDescription('');
        setColor('neutral');
        setIcon('');
      }
    }
  }, [open, category]);

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEditing) {
      const newSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(newSlug);
    }
  };

  const handleSubmit = async () => {
    if (isEditing && category) {
      await onSave(
        {
          name,
          description: description || null,
          color,
          icon: icon || null,
        } as UpdateTemplateCategoryDto,
        category.id
      );
    } else {
      await onSave({
        slug,
        name,
        description: description || null,
        color,
        icon: icon || null,
        type,
      } as CreateTemplateCategoryDto);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Category' : 'Create Category'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Sales & Contracts"
            />
          </div>

          {!isEditing && (
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g., sales-contracts"
                className="font-mono"
              />
              <p className="text-xs text-slate-500">
                URL-friendly identifier. Lowercase letters, numbers, and hyphens only.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLOR_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <span className={cn('px-2 py-0.5 rounded text-xs', opt.class)}>
                        {opt.label}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="icon">Icon (Lucide icon name)</Label>
            <Input
              id="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="e.g., file-text, briefcase, heart-pulse"
            />
            <p className="text-xs text-slate-500">
              Optional. Use a valid{' '}
              <a
                href="https://lucide.dev/icons/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                Lucide icon name
              </a>
              .
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || (!isEditing && !slug.trim()) || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Create Category'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export function CategoriesManager() {
  const [activeTab, setActiveTab] = useState<TemplateCategoryType>('document');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TemplateCategoryEntity | null>(null);

  const {
    formCategories,
    documentCategories,
    isLoadingFormCategories,
    isLoadingDocumentCategories,
    isPerformingAction,
    loadFormCategories,
    loadDocumentCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    refreshAll,
  } = useCategoriesStore();

  const isLoading = useCategoriesLoading();

  // Load categories on mount
  useEffect(() => {
    loadFormCategories(true);
    loadDocumentCategories(true);
  }, [loadFormCategories, loadDocumentCategories]);

  const currentCategories = activeTab === 'form' ? formCategories : documentCategories;
  const isLoadingCurrent = activeTab === 'form' ? isLoadingFormCategories : isLoadingDocumentCategories;

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setModalOpen(true);
  };

  const handleEditCategory = (category: TemplateCategoryEntity) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  const handleToggleActive = async (category: TemplateCategoryEntity) => {
    await updateCategory(category.id, { isActive: !category.isActive });
  };

  const handleDeleteCategory = async (category: TemplateCategoryEntity) => {
    if (confirm(`Are you sure you want to delete "${category.name}"? This will mark it as inactive.`)) {
      await deleteCategory(category.id);
    }
  };

  const handleSave = async (dto: CreateTemplateCategoryDto | UpdateTemplateCategoryDto, id?: string) => {
    if (id) {
      await updateCategory(id, dto as UpdateTemplateCategoryDto);
    } else {
      await createCategory(dto as CreateTemplateCategoryDto);
    }
  };

  const handleReorder = async (newOrder: TemplateCategoryEntity[]) => {
    // Update local state immediately for responsive UI
    if (activeTab === 'form') {
      useCategoriesStore.setState({ formCategories: newOrder });
    } else {
      useCategoriesStore.setState({ documentCategories: newOrder });
    }

    // Persist to server
    const ids = newOrder.map((c) => c.id);
    await reorderCategories(activeTab, ids);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Tags className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Template Categories
              </h1>
              <p className="text-sm text-slate-500">
                Manage categories for form and document templates
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refreshAll()}
              disabled={isLoading}
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
            <Button onClick={handleCreateCategory}>
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 lg:px-6 pb-4">
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'document' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('document')}
              className="gap-2"
            >
              <FileText className="h-4 w-4" />
              Document Categories ({documentCategories.filter((c) => c.isActive).length})
            </Button>
            <Button
              variant={activeTab === 'form' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('form')}
              className="gap-2"
            >
              <ClipboardList className="h-4 w-4" />
              Form Categories ({formCategories.filter((c) => c.isActive).length})
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        {isLoadingCurrent ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-slate-500">Loading categories...</p>
            </div>
          </div>
        ) : currentCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Tags className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-600 dark:text-slate-400 font-medium">
              No categories yet
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Create your first {activeTab} category to get started.
            </p>
            <Button onClick={handleCreateCategory} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <p className="text-sm text-slate-500 mb-4">
              Drag categories to reorder them. The order determines how they appear in template editors.
            </p>
            <Reorder.Group
              axis="y"
              values={currentCategories}
              onReorder={handleReorder}
              className="space-y-2"
            >
              {currentCategories.map((category) => (
                <CategoryRow
                  key={category.id}
                  category={category}
                  onEdit={handleEditCategory}
                  onDelete={handleDeleteCategory}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </Reorder.Group>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <CategoryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        category={editingCategory}
        type={activeTab}
        onSave={handleSave}
        isSaving={isPerformingAction}
      />
    </motion.div>
  );
}
